import * as React from "react";
import { LoaderCircle } from "lucide-react";
import { mergeClassNames } from "./class-names";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "accent"
  | "danger"
  | "outline"
  | "brandSubtle"
  | "subtle"
  | "ghost";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonStyleOptions {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly className?: string | undefined;
}

export function buttonClassNames({
  variant = "primary",
  size = "md",
  className,
}: ButtonStyleOptions = {}): string {
  return mergeClassNames(
    "inline-flex cursor-pointer items-center justify-center rounded-md font-semibold transition-[background-color,border-color,color,box-shadow,transform] duration-[var(--motion-duration-fast)] ease-[var(--motion-easing-standard)] active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface motion-reduce:transform-none motion-reduce:transition-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 disabled:active:translate-y-0 aria-disabled:pointer-events-none aria-disabled:cursor-not-allowed aria-disabled:opacity-50",
    {
      "bg-action-primary text-action-primary-ink hover:bg-action-primary-hover active:bg-action-primary-pressed":
        variant === "primary",
      "border border-border-default bg-action-secondary text-action-secondary-ink hover:bg-action-secondary-hover active:bg-action-secondary-pressed":
        variant === "secondary",
      "bg-accent text-accent-ink hover:bg-accent-hover active:bg-accent-pressed":
        variant === "accent",
      "bg-danger text-danger-ink hover:brightness-95 active:brightness-90":
        variant === "danger",
      "border border-border-default bg-transparent text-primary hover:bg-subtle active:bg-action-secondary-pressed":
        variant === "outline",
      "border border-brand bg-brand-subtle text-brand hover:bg-brand hover:text-on-brand active:bg-accent-pressed active:text-on-brand":
        variant === "brandSubtle",
      "border border-border-default bg-subtle text-primary hover:bg-surface active:bg-action-secondary-pressed":
        variant === "subtle",
      "bg-transparent text-primary hover:bg-subtle active:bg-action-secondary-pressed":
        variant === "ghost",
    },
    {
      "text-body min-h-11 px-3": size === "sm",
      "text-body min-h-11 px-4": size === "md",
      "text-body-large min-h-12 px-6": size === "lg",
    },
    className,
  );
}

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className,
      disabled,
      loading = false,
      variant = "primary",
      size = "md",
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        className={buttonClassNames({ variant, size, className })}
        aria-busy={loading || undefined}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <LoaderCircle
            className="me-2 size-4 animate-spin motion-reduce:animate-none"
            aria-hidden="true"
          />
        ) : null}
        {children}
      </button>
    );
  },
);
Button.displayName = "Button";
